import {useEffect, useRef, useState} from 'react';
import {findRuTimezone} from '../../config/timezones';
import {usePaywallPurchase} from '../../hooks/usePaywallPurchase';

// The pack we highlight as "лучший выбор" when it's available.
const RECOMMENDED_TOPUP_ID = 'zvonok_1';

// Short marketing tagline per pack id (falls back to the plan name).
const PLAN_TAGLINES = {
    zvonok_1: 'Личный звонок от звезды в нужную минуту',
};

// Russian plural for "звонок": 1 → звонок, 2–4 → звонка, 5+/0 → звонков.
function zvonokLabel(n) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return `${n} звонок`;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} звонка`;
    return `${n} звонков`;
}

// «29 июля, 12:00 (Владивосток)» — время заявки в выбранном при заказе поясе.
function formatSummaryDateTime(iso, timeZoneId) {
    const opts = {day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'};
    let formatted;
    try {
        formatted = new Date(iso).toLocaleString('ru-RU', {...opts, timeZone: timeZoneId || undefined});
    } catch {
        try {
            formatted = new Date(iso).toLocaleString('ru-RU', opts);
        } catch {
            return iso;
        }
    }
    const city = findRuTimezone(timeZoneId)?.city;
    return city ? `${formatted} (${city})` : formatted;
}

// «+79991234567» → «+7 999 123-45-67».
function formatSummaryPhone(phone) {
    const m = String(phone || '').match(/^\+7(\d{3})(\d{3})(\d{2})(\d{2})$/);
    return m ? `+7 ${m[1]} ${m[2]}-${m[3]}-${m[4]}` : phone;
}

const FEATURE_ICON_PROPS = {
    width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
    stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round',
};
const FEATURES = [
    {
        icon: (
            <svg {...FEATURE_ICON_PROPS}>
                <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
        ),
        title: 'Голос звезды',
        text: 'Клонированный голос звучит как настоящий звонок.',
    },
    {
        icon: (
            <svg {...FEATURE_ICON_PROPS}>
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
            </svg>
        ),
        title: 'Точно вовремя',
        text: 'Позвоним в выбранную минуту, дозваниваемся до трёх раз.',
    },
    {
        icon: (
            <svg {...FEATURE_ICON_PROPS}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M9 12l2 2 4-4"/>
            </svg>
        ),
        title: '100% гарантия возврата',
        text: 'Не дозвонились — кредит вернётся автоматически.',
    },
];

// Пейвол звонков: одна колонка, без превью — только выбор пакета и оплата.
// Тарифы полностью независимы от видеопоздравлений (product=zvonki).
export default function ZvonkiPaywall({
                                          isOpen = true,
                                          onClose,
                                          plans = {subscriptions: [], topups: []},
                                          // Заполненная заявка на звонок: уходит в /celebs/topup, чтобы вебхук
                                          // оплаты запланировал звонок автоматически.
                                          pendingCall = null,
                                          onPurchaseSuccess,
                                      }) {
    const activePlans = plans.topups || [];
    const recommendedId = activePlans.find(p => p.id === RECOMMENDED_TOPUP_ID)?.id
        || activePlans[activePlans.length - 1]?.id;

    const [selectedPlanId, setSelectedPlanId] = useState(null);

    const {
        isPurchasing,
        purchaseError,
        showYooKassaWidget,
        yookassaWidgetRef,
        widgetContainerId,
        startPurchase,
        closeYooKassaWidget,
    } = usePaywallPurchase({
        onSuccess: onPurchaseSuccess,
        widgetContainerId: 'yookassa-widget-container-zvonki',
    });

    // Keep a valid pack selected.
    useEffect(() => {
        if (activePlans.length === 0) return;
        if (activePlans.some(p => p.id === selectedPlanId)) return;
        setSelectedPlanId(recommendedId || activePlans[0].id);
    }, [activePlans, recommendedId, selectedPlanId]);

    // «Послушать ещё раз» прямо в пейволе: аудио превью уже лежит на S3,
    // напоминание о вау-моменте в момент оплаты ничего не стоит.
    const [isPlayingPreview, setIsPlayingPreview] = useState(false);
    const previewAudioRef = useRef(null);
    const stopPreviewAudio = () => {
        previewAudioRef.current?.pause();
        previewAudioRef.current = null;
        setIsPlayingPreview(false);
    };
    const togglePreviewAudio = () => {
        if (isPlayingPreview) {
            stopPreviewAudio();
            return;
        }
        const audio = new Audio(pendingCall.audioUrl);
        previewAudioRef.current = audio;
        setIsPlayingPreview(true);
        audio.onended = () => setIsPlayingPreview(false);
        audio.play().catch(() => setIsPlayingPreview(false));
    };
    // Модалка не размонтируется при закрытии (isOpen=false) — глушим звук сами.
    useEffect(() => {
        if (!isOpen) {
            previewAudioRef.current?.pause();
            previewAudioRef.current = null;
            setIsPlayingPreview(false);
        }
    }, [isOpen]);
    useEffect(() => () => previewAudioRef.current?.pause(), []);

    if (!isOpen) return null;

    const selectedPlan = activePlans.find(p => p.id === selectedPlanId) || null;

    const handlePurchase = () => {
        if (!selectedPlan) return;
        startPurchase({
            planId: selectedPlan.id,
            credits: selectedPlan.calls,
            description: `Покупка: ${zvonokLabel(selectedPlan.calls)} от звезды`,
            analyticsEvent: 'start_topup_zvonki',
            zvonokCall: pendingCall ? {
                celeb_id: pendingCall.celebId,
                phone: pendingCall.phone,
                call_text: pendingCall.callText,
                audio_url: pendingCall.audioUrl,
                audio_duration_sec: pendingCall.audioDurationSec,
                scheduled_at: pendingCall.scheduledAt,
                timezone: pendingCall.timezone,
            } : undefined,
        });
    };

    const ctaText = isPurchasing
        ? 'Обработка…'
        : `Купить ${zvonokLabel(selectedPlan?.calls || 1)}`;

    return (
        <div
            className="fixed inset-0 z-50 flex items-start lg:items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="w-full max-w-[560px] my-6 lg:my-auto bg-[#16121f] rounded-[24px] p-5 sm:p-6 shadow-2xl text-white flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-2xl font-bold leading-tight">Звонки от ИИ-звёзд</h3>
                    <button
                        onClick={onClose}
                        aria-label="Закрыть"
                        className="-mt-1 -mr-1 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white/70 flex items-center justify-center shrink-0 transition"
                    >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                  strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>
                <p className="text-[15px] text-white/60 leading-relaxed mb-5">
                    Купите звонок — платите один раз, без подписки. Кредиты не сгорают
                    и остаются на балансе, пока вы их не используете.
                </p>

                {/* Сводка заявки: подтверждение заказа, а не ввод — все данные
                    уже собраны в билдере и уедут вместе с покупкой. */}
                {pendingCall && (
                    <div className="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3 mb-5">
                        <p className="text-sm text-white/85 leading-snug">
                            {pendingCall.persona?.name
                                ? `${pendingCall.persona.name} позвонит `
                                : 'Позвоним '}
                            {formatSummaryDateTime(pendingCall.scheduledAt, pendingCall.timezone)}
                            {' на '}{formatSummaryPhone(pendingCall.phone)}
                        </p>
                        {pendingCall.audioUrl && (
                            <button
                                type="button"
                                onClick={togglePreviewAudio}
                                className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-red hover:opacity-80 transition"
                            >
                                {isPlayingPreview ? (
                                    <>
                                        <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
                                            <rect x="2" y="2" width="3.5" height="10" rx="1"/>
                                            <rect x="8.5" y="2" width="3.5" height="10" rx="1"/>
                                        </svg>
                                        Остановить
                                    </>
                                ) : (
                                    <>
                                        <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
                                            <path d="M3 2l9 5-9 5V2z"/>
                                        </svg>
                                        Послушать превью ещё раз
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                )}

                <p className="text-lg font-bold mb-3">Выберите пакет</p>
                {activePlans.length === 0 ? (
                    <div
                        className="rounded-2xl bg-white/5 border border-white/10 px-4 py-6 text-center text-white/50 text-sm">
                        Загружаем тарифы…
                    </div>
                ) : (
                    <div
                        className={`grid gap-3 ${activePlans.length >= 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                        {activePlans.map(plan => {
                            const isSelected = selectedPlanId === plan.id;
                            const isRecommended = activePlans.length > 1 && plan.id === recommendedId;
                            const tagline = PLAN_TAGLINES[plan.id] || plan.name || '';
                            // Per-call price for multi-call packs (no point on a single call).
                            const perCallPrice = plan.calls > 1
                                ? Math.round(plan.price / plan.calls)
                                : null;
                            return (
                                <button
                                    key={plan.id}
                                    onClick={() => setSelectedPlanId(plan.id)}
                                    className={`relative text-left rounded-2xl p-4 border-2 transition ${
                                        isSelected
                                            ? 'border-brand-red bg-brand-red/[0.08]'
                                            : 'border-white/10 bg-white/[0.04] hover:border-white/25'
                                    }`}
                                >
                                    {isRecommended && (
                                        <span
                                            className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                                            лучший выбор
                                        </span>
                                    )}
                                    <span
                                        className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                            isSelected ? 'border-brand-red' : 'border-white/30'
                                        }`}
                                    >
                                        {isSelected && <span className="w-2 h-2 rounded-full bg-brand-red"/>}
                                    </span>

                                    <p className="text-lg font-bold leading-tight pr-5">{zvonokLabel(plan.calls)}</p>
                                    <p className="text-xs text-white/55 mt-0.5 mb-3 leading-snug">{tagline}</p>

                                    <div className="flex items-baseline gap-1">
                                        <span className="text-2xl font-bold leading-none">
                                            {plan.price.toLocaleString('ru-RU')}
                                        </span>
                                        <span className="text-sm text-white/55">₽</span>
                                    </div>
                                    {perCallPrice && (
                                        <p className="text-xs text-white/55 mt-1">
                                            {perCallPrice.toLocaleString('ru-RU')} ₽ за звонок
                                        </p>
                                    )}
                                    {(plan.oldPrice || plan.discount) && (
                                        <div className="mt-1.5 flex items-center gap-2">
                                            {plan.oldPrice && (
                                                <span className="text-xs text-white/40 line-through">
                                                    {plan.oldPrice.toLocaleString('ru-RU')} ₽
                                                </span>
                                            )}
                                            {plan.discount && (
                                                <span
                                                    className="bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                                    -{plan.discount}%
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    <p className="text-[11px] leading-snug text-white/45 mt-2">Разовый платёж, без
                                        подписки</p>
                                </button>
                            );
                        })}
                    </div>
                )}

                {activePlans.length > 0 && (
                    <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
                        {FEATURES.map(f => (
                            <div key={f.title}>
                                <span className="text-brand-red">{f.icon}</span>
                                <p className="text-[13px] font-semibold leading-tight mt-1.5">{f.title}</p>
                                <p className="text-[11px] text-white/55 leading-snug mt-1">{f.text}</p>
                            </div>
                        ))}
                    </div>
                )}

                {purchaseError && !isPurchasing && (
                    <div
                        className="mt-4 rounded-xl bg-red-500/15 border border-red-400/30 px-4 py-3 text-sm text-red-200">
                        {purchaseError}
                    </div>
                )}

                <button
                    onClick={handlePurchase}
                    disabled={isPurchasing || !selectedPlan}
                    className="mt-5 w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold tracking-[-0.16px] hover:opacity-95 transition disabled:opacity-50"
                >
                    {ctaText}
                </button>
                <p className="mt-3 text-center text-xs text-white/45">
                    Безопасная оплата · СБП и карты
                </p>
            </div>

            {showYooKassaWidget && (
                <div
                    className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={e => e.stopPropagation()}
                >
                    <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                        <button
                            onClick={closeYooKassaWidget}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                        <h3 className="text-xl font-bold text-brand-dark mb-4">Оплата</h3>
                        <div id={widgetContainerId} ref={yookassaWidgetRef}></div>
                    </div>
                </div>
            )}
        </div>
    );
}
